import LabelTableCell from 'Components/LabelTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { ServiceProviderTypeValue } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderTypeValue'
import type { ServiceProvider } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import { ServiceProviderType } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderSlice'
import { HSL_ENABLED_FEATURES } from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import InfoIcon from '@mui/icons-material/Info'
import {
  Chip,
  type ChipProps,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip
} from '@mui/material'
import { t } from 'ttag'

const StyledTableCell = styled(LabelTableCell)`
  width: 40%;
`

interface ServiceProviderDetailsProps {
  serviceProvider: ServiceProvider
}

function ServiceProviderDetails({ serviceProvider }: Readonly<ServiceProviderDetailsProps>) {
  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    ACTIVE: { color: 'success', label: t`Active` },
    PASSIVE: { color: 'default', label: t`Passive` },
    YES: { color: 'success', label: t`Yes` },
    NO: { color: 'error', label: t`No` }
  }

  function getChipPropsByActivity(status: boolean) {
    return chipPropsMapping[status ? 'ACTIVE' : 'PASSIVE']
  }

  function getChipPropsByYesNo(field: boolean) {
    return chipPropsMapping[field ? 'YES' : 'NO']
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ td: { border: 'none' } }} aria-label={t`Details table`}>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell>{t`Code`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(serviceProvider.service_provider_id)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Service provider name`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(serviceProvider.name)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Service provider type`}</StyledTableCell>
            <TableCell>
              <ServiceProviderTypeValue serviceProviderType={serviceProvider.type} />
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Status`}</StyledTableCell>
            <TableCell>
              {<Chip size="small" {...getChipPropsByActivity(serviceProvider.active)} />}
              {serviceProvider.activity_end_date && serviceProvider.active && (
                <Tooltip
                  title={
                    t`Activity ends` + ' ' + datetimeFormatter(serviceProvider.activity_end_date)
                  }
                  placement="top"
                >
                  <IconButton>
                    <InfoIcon color="error" />
                  </IconButton>
                </Tooltip>
              )}
            </TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Created`}</StyledTableCell>
            <TableCell>{datetimeFormatter(serviceProvider.created_timestamp)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Start date`}</StyledTableCell>
            <TableCell>{datetimeFormatter(serviceProvider.start_date)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Activity end date`}</StyledTableCell>
            <TableCell>{datetimeFormatter(serviceProvider.activity_end_date)}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Last modified`}</StyledTableCell>
            <TableCell>{datetimeFormatter(new Date(serviceProvider.modified_timestamp))}</TableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell>{t`Business id`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(serviceProvider.business_id)}</TableCell>
          </StyledTableRow>
          {HSL_ENABLED_FEATURES && (
            <>
              <StyledTableRow>
                <StyledTableCell>{t`S365 account id`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.s365t_account_id)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Customer reference name`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.customer_reference_name)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Billed at sales point level`}</StyledTableCell>
                <TableCell>
                  {
                    <Chip
                      size="small"
                      {...getChipPropsByYesNo(serviceProvider.billed_at_sales_point_level)}
                    />
                  }
                </TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Transfer sales to financial system`}</StyledTableCell>
                <TableCell>
                  {
                    <Chip
                      size="small"
                      {...getChipPropsByYesNo(serviceProvider.transfer_sales_to_financial_system)}
                    />
                  }
                </TableCell>
              </StyledTableRow>
            </>
          )}
          {serviceProvider.type === ServiceProviderType.SERVICE_POINT && (
            <>
              <StyledTableRow>
                <StyledTableCell>{t`Register file path`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.register_file_path)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Register file name`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.register_file_name)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Register OK file name`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.register_ok_file_name)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Register not OK file name`}</StyledTableCell>
                <TableCell>
                  {showValueOrHyphen(serviceProvider.register_not_ok_file_name)}
                </TableCell>
              </StyledTableRow>
            </>
          )}
          {!HSL_ENABLED_FEATURES && (
            <>
              <StyledTableRow>
                <StyledTableCell>{t`CPU url`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.cpu_url)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Pusatec channel`}</StyledTableCell>
                <TableCell>{showValueOrHyphen(serviceProvider.pusatec_channel)}</TableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>{t`Vrk queries enabled`}</StyledTableCell>
                <TableCell>
                  {
                    <Chip
                      size="small"
                      {...getChipPropsByYesNo(serviceProvider.vrk_queries_enabled)}
                    />
                  }
                </TableCell>
              </StyledTableRow>
            </>
          )}
          <StyledTableRow>
            <StyledTableCell>{t`Source`}</StyledTableCell>
            <TableCell>{showValueOrHyphen(serviceProvider.source)}</TableCell>
          </StyledTableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ServiceProviderDetails
